import React, { useEffect, useRef, useState } from 'react'
import { Box, Grid, Skeleton, Typography } from '@mui/material'
// eslint-disable-next-line no-unused-vars
import TagFilterComponent from './TagFilterComponent'
import MediaList from './MediaList'
import { DropDownElement } from '../DropDownComponent'
import Pagination from '../Pagination'
import useGalleryWidth from '../util/useGalleryWidth'
import { useTranslation } from 'react-i18next'
import { MediaFileMetadata } from '@3m5/sm_publish-frontend-api'

// Extend the MediaFileMetadata type to include tags
export interface ExtendedMediaFileMetadata extends MediaFileMetadata {
  tags?: Array<{id: string, label: string}>
}

interface IProps {
  mediaData: ExtendedMediaFileMetadata[] | { content?: ExtendedMediaFileMetadata[], items?: ExtendedMediaFileMetadata[] }
  selected: any
  wideView?: boolean
  setSelected: (item: any, thumbnail: string | undefined) => void
  activateTagSearch?: boolean
  useRoundImageBorders?: boolean
  doNotShowAsSelected?: boolean
  itemsProPage?: number
  rows?: number
  onPageChange?: (page: number) => void
  totalCount?: number
}

export interface GalleryFilter {
  tags: DropDownElement[]
}

const MediaGalleryComponent: React.FC<IProps> = (props) => {
  const [filter, setFilter] = useState<GalleryFilter>({ tags: [] })
  const [data, setData] = useState<ExtendedMediaFileMetadata[]>()
  const [page, setPage] = useState(1)
  const [filtered, setFiltered] = useState(false)
  const { t } = useTranslation()

  const mediaContainerRef = useRef<HTMLDivElement>(null)
  const { itemsPerPage, elementSize } = useGalleryWidth(
    mediaContainerRef,
    undefined,
    props.wideView ? 250 : undefined,
    props.itemsProPage,
    props.rows
  )

  // Helper function to extract media items from different data structures
  const extractMediaItems = (data: any): ExtendedMediaFileMetadata[] => {
    if (Array.isArray(data)) {
      return data
    } else if (data && typeof data === 'object') {
      if (data.content && Array.isArray(data.content)) {
        return data.content
      } else if (data.items && Array.isArray(data.items)) {
        return data.items
      }
    }
    return []
  }

  const mediaListView = () => {
    if (filtered && filter.tags.length > 0) {
      filterGalleryData(props.mediaData)
    } else {
      setFiltered(false)
      // If onPageChange is provided, we're doing server-side pagination
      if (props.onPageChange) {
        const mediaItems = extractMediaItems(props.mediaData)
        setData(mediaItems)
      } else {
        handlePageChange(props.mediaData)
      }
    }
  }

  const filterGalleryData = (fData: any) => {
    const mediaItems = extractMediaItems(fData)
    const tempData: ExtendedMediaFileMetadata[] = []

    if (mediaItems.length > 0) {
      mediaItems.forEach((elem: ExtendedMediaFileMetadata) => {
        let elemExist = false
        if (elem.tags) {
          elem.tags.forEach((tag: any) => {
            if ((filter.tags.findIndex(filterTag => filterTag.key === tag.id)) !== -1) {
              elemExist = true
            }
          })
        }
        if (elemExist) tempData.push(elem)
      })

      setPage(1)
      // If onPageChange is provided, we're doing server-side pagination
      if (props.onPageChange) {
        // Call onPageChange with the new page (1)
        props.onPageChange(1)
        setData(tempData)
      } else {
        handlePageChange(tempData)
      }
    }
  }

  const handlePageChange = (mData: any, page = 1) => {
    const mediaItems = extractMediaItems(mData)

    if (mediaItems.length > 0) {
      // If onPageChange is provided, we're doing server-side pagination
      // and the parent component will provide the correct data slice
      if (!props.onPageChange) {
        const startItem = (page - 1) * itemsPerPage
        const endItem = page * itemsPerPage
        setData(mediaItems.slice(startItem, endItem))
      } else {
        // For server-side pagination, just set the data as is
        setData(mediaItems)
      }
    }
  }

  const handleFilter = (tags: GalleryFilter) => {
    setFilter(tags)
    setFiltered(true)
  }

  useEffect(() => {
    mediaListView()
  }, [filter, props.mediaData, itemsPerPage])

  useEffect(() => {
    // If onPageChange is provided, it will handle the pagination
    // Otherwise, do client-side pagination
    if (!props.onPageChange) {
      handlePageChange(props.mediaData, page)
    }
  }, [page])

  // Avoid empty pages in server-side pagination: if server returns no data for the current page,
  // step back one page automatically.
  useEffect(() => {
    if (props.onPageChange) {
      const length = Array.isArray(data) ? data.length : 0
      if (length === 0 && page > 1) {
        const prevPage = page - 1
        setPage(prevPage)
        props.onPageChange(prevPage)
      }
    }
  }, [data])

  const computeTotalItems = () => {
    if (props.onPageChange) {
      // For server-side pagination, never compute totals; use backend-provided totals only
      if (props.totalCount !== undefined) {
        return props.totalCount
      }
      if (typeof props.mediaData === 'object' && !Array.isArray(props.mediaData) && props.mediaData && 'totalElements' in props.mediaData) {
        return (props.mediaData as any).totalElements || 0
      }
      return 0
    } else {
      if (props.totalCount !== undefined) return props.totalCount
      if (filtered) return Array.isArray(data) ? data.length : 0
      if (typeof props.mediaData === 'object' && !Array.isArray(props.mediaData) && 'totalElements' in props.mediaData) {
        return (props.mediaData as any).totalElements
      }
      return extractMediaItems(props.mediaData).length
    }
  }

  return (
    <Grid container ref={mediaContainerRef}>
      {data
        ? (
          <>
            <Grid xs={12} item>
              <Box component='div' style={{ marginBottom: '24px' }}>
                {props.activateTagSearch && <TagFilterComponent handleFilter={handleFilter} mediaData={undefined} />}
              </Box>
            </Grid>
            <Grid xs={12} item>
              <Box component='div'>
                {data && Array.isArray(data) && data.length > 0 ? (
                  <MediaList elementSize={elementSize} setSelected={props.setSelected} selected={props.selected} mediaData={data} useRoundImageBorders={props.useRoundImageBorders} doNotShowAsSelected={props.doNotShowAsSelected} />
                ) : (
                  <Box sx={{ minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{t('postingCommon:noMediaData')}</Box>
                )}
              </Box>
            </Grid>
            <Grid xs={12} item>
              <Box component='div' style={{ display: 'flex', justifyContent: 'center' }}>
                {props.mediaData && (
                  <Pagination
                    totalItems={computeTotalItems()}
                    itemsPerPage={itemsPerPage}
                    page={page}
                    setPage={(newPage) => {
                      setPage(newPage)
                      // Always call onPageChange if it exists, regardless of whether the page changed
                      if (props.onPageChange) {
                        props.onPageChange(newPage)
                      }
                    }}
                  />
                )}
              </Box>
            </Grid>
          </>
        )
        : (
          <>
            <Skeleton style={{ marginBottom: '24px' }} variant='rectangular' width='100%' height={48} />
            <Box style={{ display: 'flex', flexWrap: 'wrap' }}>
              {[...Array(itemsPerPage)].map((x, i) =>
                <Skeleton key={i} style={{ margin: '2.5px' }} variant='rectangular' width={elementSize} height={105} />,
              )}
            </Box>
            <Box style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
              <Skeleton style={{ marginTop: '24px' }} variant='rectangular' width={305} height={25} />
            </Box>
          </>
        )}
    </Grid>
  )
}

export default MediaGalleryComponent
